import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { routes } from 'routes/routes';
import { apiClient, isDefaultResponseErrorDataObject } from 'common/services';
import type { ConsentsLocationState } from '../page-consents';
import type { OnboardingTourLocationState } from '../page-onboarding-tour';

export const CONSENTS_ERROR_STATUS_CODE = 412;

export const PRECONDITION_FAILED_ONBOARDING = 'onboarding_required';

export const PRECONDITION_FAILED_CONSENTS = 'consents_required';

const PreconditionFailedGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);

  navigateRef.current = navigate;

  locationRef.current = location;

  const interceptorIdRef = useRef<null | number>(null);

  const navigateToConsents = async () => {
    const currentLocation = locationRef.current;

    if (currentLocation.pathname !== routes.consents) {
      await navigateRef.current(routes.consents, {
        state: {
          originalState: currentLocation.state,
          referrer: currentLocation.pathname + currentLocation.search,
        } satisfies ConsentsLocationState,
      });
    }
  };

  const navigateToOnboarding = async () => {
    const currentLocation = locationRef.current;

    if (currentLocation.pathname !== routes.onboardingTour.index) {
      await navigateRef.current(routes.onboardingTour.index, {
        state: {
          originalState: currentLocation.state,
          referrer: currentLocation.pathname + currentLocation.search,
        } satisfies OnboardingTourLocationState,
      });
    }
  };

  const handlePreconditionFailedMessage = async (message?: string) => {
    if (message) {
      const parts = message.split(',');

      if (parts.includes(PRECONDITION_FAILED_ONBOARDING)) {
        await navigateToOnboarding();
        return;
      }

      if (parts.includes(PRECONDITION_FAILED_CONSENTS)) {
        await navigateToConsents();
        return;
      }
    }

    await navigateToConsents();
  };

  const handleAxiosResponseOrError = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    responseOrError: any,
  ) => {
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      responseOrError?.status === CONSENTS_ERROR_STATUS_CODE ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      responseOrError?.response?.status === CONSENTS_ERROR_STATUS_CODE
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      const { data, response } = responseOrError;

      if (response) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const { data: responseData } = response;

        if (isDefaultResponseErrorDataObject(responseData)) {
          await handlePreconditionFailedMessage(responseData.message);
          return;
        }
      }

      if (isDefaultResponseErrorDataObject(data)) {
        await handlePreconditionFailedMessage(data.message);
        return;
      }

      await handlePreconditionFailedMessage();
    }
  };

  interceptorIdRef.current ??= apiClient.interceptors.response.use(undefined, async (error) => {
    await handleAxiosResponseOrError(error);

    throw error;
  });

  useEffect(
    () => () => {
      if (interceptorIdRef.current !== null) {
        apiClient.interceptors.response.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    },
    [],
  );

  return <Outlet />;
};

export default PreconditionFailedGuard;
